import React from 'react';
import { Platform, ScrollView, StyleSheet, Switch, Text, View } from 'react-native';

interface Underlying {
  name: string;
  marketPrice: number;
  performance: number;
  initialFixingLevel: number;
  strikeLevel: number;
  strikeDistance: number;
  barrierLevel: number;
  barrierDistance: number;
  autocallDistance: number | null;
  couponBarrierDistance: number | null;
  underlyingCurrency?: string;
  upperStrikePct: number | null;
}

interface ProductUnderlyingsInfoProps {
  underlyings: Underlying[];
  visibleUnderlyings: boolean[];
  currency: string;
  onToggleVisibility: (index: number) => void;
  isDesktopCombined?: boolean;
  showDistanceCouponBarrier?: boolean;
  showBarrierLevel?: boolean;
  showBarrierDistance?: boolean;
  showAutocallDistance?: boolean;
  underlyingsProps?: Record<string, boolean>;
  showUpperStrike?: boolean;
  showStrikeLevel?: boolean;
  showStrikeDistance?: boolean;
}

export const ProductUnderlyingsInfo: React.FC<ProductUnderlyingsInfoProps> = ({
  underlyings,
  visibleUnderlyings,
  currency,
  onToggleVisibility,
  isDesktopCombined = false,
  showDistanceCouponBarrier,
  showBarrierLevel = true,
  showBarrierDistance = true,
  showAutocallDistance = false,
  underlyingsProps = {},
  showUpperStrike,
  showStrikeDistance,
  showStrikeLevel,
}) => {
  if (!underlyings || underlyings.length === 0) return null;

  // Helper function to check if a prop should be shown
  const shouldShow = (propName: string): boolean => {
    return underlyingsProps[propName] !== false;
  };

  return (
    <View
      style={[
        isDesktopCombined ? styles.containerCombined : styles.container,
        Platform.OS === 'web' && !isDesktopCombined && { padding: 16, marginBottom: 12 },
      ]}
    >
      <View style={styles.header}>
        <Text style={styles.sectionTitle}>Underlyings</Text>
      </View>
      <View style={styles.separator} />

      {isDesktopCombined && Platform.OS === 'web' ? (
        <View style={styles.cardsGrid}>
          {underlyings.map((underlying, index) => {
            const displayCurrency = underlying.underlyingCurrency || currency;
            const isVisible = visibleUnderlyings[index] !== false;

            return (
              <View key={`${underlying.name}-${index}`} style={[styles.card, styles.cardDesktop]}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardTitle} numberOfLines={1}>
                    {underlying.name}
                  </Text>
                  <Switch
                    value={isVisible}
                    onValueChange={() => onToggleVisibility(index)}
                    trackColor={{ false: '#E0E0E0', true: '#00506E' }}
                    thumbColor="#FFFFFF"
                    ios_backgroundColor="#E0E0E0"
                    style={styles.switch}
                  />
                </View>

                <View style={styles.content}>
                  <View style={styles.row}>
                    <Text style={styles.label}>Market price</Text>
                    <Text style={styles.value}>
                      {underlying.marketPrice
                        ? `${displayCurrency} ${underlying.marketPrice.toFixed(2)}`
                        : '-'}
                    </Text>
                  </View>
                  <View style={styles.row}>
                    <Text style={styles.label}>Performance</Text>
                    <Text style={[styles.value, underlying.performance < 0 && styles.negativeValue]}>
                      {underlying.performance?.toFixed(2) || '0.00'}%
                    </Text>
                  </View>
                  <View style={styles.row}>
                    <Text style={styles.label}>Initial fixing level</Text>
                    <Text style={styles.value}>
                      {underlying.initialFixingLevel
                        ? `${displayCurrency} ${underlying.initialFixingLevel.toFixed(2)}`
                        : '-'}
                    </Text>
                  </View>
                  {shouldShow('strikeLevel') && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Strike level</Text>
                      <Text style={styles.value}>
                        {underlying.strikeLevel ? `${displayCurrency} ${underlying.strikeLevel.toFixed(2)}` : '-'}
                      </Text>
                    </View>
                  )}
                  {shouldShow('strikeDistance') && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Strike distance</Text>
                      <Text style={[styles.value, underlying.strikeDistance < 0 && styles.negativeValue]}>
                        {((underlying.strikeDistance || 0) * 100).toFixed(2)}%
                      </Text>
                    </View>
                  )}
                  {showUpperStrike && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Upper Strike</Text>
                      <Text style={styles.value}>
                        {underlying.upperStrikePct ? `${underlying.upperStrikePct.toFixed(2)}%` : '-'}
                      </Text>
                    </View>
                  )}
                  {showBarrierLevel && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Barrier Level</Text>
                      <Text style={styles.value}>
                        {underlying.barrierLevel ? `${displayCurrency} ${underlying.barrierLevel.toFixed(2)}` : '-'}
                      </Text>
                    </View>
                  )}
                  {showBarrierDistance && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Barrier distance</Text>
                      <Text style={[styles.value, underlying.barrierDistance < 0 && styles.negativeValue]}>
                        {underlying.barrierDistance?.toFixed(2) || '0.00'}%
                      </Text>
                    </View>
                  )}
                  {showAutocallDistance && underlying.autocallDistance != null && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Distance from autocall</Text>
                      <Text style={[styles.value, underlying.autocallDistance < 0 && styles.negativeValue]}>
                        {((underlying.autocallDistance || 0) * 100).toFixed(2)}%
                      </Text>
                    </View>
                  )}
                  {showDistanceCouponBarrier && underlying.couponBarrierDistance && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Coupon barrier distance</Text>
                      <Text style={[styles.value, underlying.couponBarrierDistance < 0 && styles.negativeValue]}>
                        {underlying.couponBarrierDistance?.toFixed(2) || '0.00'}%
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.cardsRow}>
          {underlyings.map((underlying, index) => {
            const displayCurrency = underlying.underlyingCurrency || currency;
            const isVisible = visibleUnderlyings[index] !== false;

            return (
              <View key={`${underlying.name}-${index}`} style={styles.card}>
                <View style={styles.cardHeader}>
                  <Text style={styles.cardTitle} numberOfLines={1}>
                    {underlying.name}
                  </Text>
                  <Switch
                    value={isVisible}
                    onValueChange={() => onToggleVisibility(index)}
                    trackColor={{ false: '#E0E0E0', true: '#00506E' }}
                    thumbColor="#FFFFFF"
                    ios_backgroundColor="#E0E0E0"
                    style={styles.switch}
                  />
                </View>

                <View style={styles.content}>
                  <View style={styles.row}>
                    <Text style={styles.label}>Market price</Text>
                    <Text style={styles.value}>
                      {underlying.marketPrice ? `${displayCurrency} ${underlying.marketPrice.toFixed(2)}` : '-'}
                    </Text>
                  </View>
                  <View style={styles.row}>
                    <Text style={styles.label}>Performance</Text>
                    <Text style={[styles.value, underlying.performance < 0 && styles.negativeValue]}>
                      {underlying.performance?.toFixed(2) || '0.00'}%
                    </Text>
                  </View>
                  <View style={styles.row}>
                    <Text style={styles.label}>Initial fixing level</Text>
                    <Text style={styles.value}>
                      {underlying.initialFixingLevel
                        ? `${displayCurrency} ${underlying.initialFixingLevel.toFixed(2)}`
                        : '-'}
                    </Text>
                  </View>
                  {shouldShow('strikeLevel') && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Strike level</Text>
                      <Text style={styles.value}>
                        {underlying.strikeLevel ? `${displayCurrency} ${underlying.strikeLevel.toFixed(2)}` : '-'}
                      </Text>
                    </View>
                  )}
                  {shouldShow('strikeDistance') && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Strike distance</Text>
                      <Text style={[styles.value, underlying.strikeDistance < 0 && styles.negativeValue]}>
                        {((underlying.strikeDistance || 0) * 100).toFixed(2)}%
                      </Text>
                    </View>
                  )}
                  {showUpperStrike && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Upper Strike</Text>
                      <Text style={styles.value}>
                        {underlying.upperStrikePct ? `${underlying.upperStrikePct.toFixed(2)}%` : '-'}
                      </Text>
                    </View>
                  )}
                  {showBarrierLevel && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Barrier Level</Text>
                      <Text style={styles.value}>
                        {underlying.barrierLevel ? `${displayCurrency} ${underlying.barrierLevel.toFixed(2)}` : '-'}
                      </Text>
                    </View>
                  )}
                  {showBarrierDistance && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Barrier distance</Text>
                      <Text style={[styles.value, underlying.barrierDistance < 0 && styles.negativeValue]}>
                        {underlying.barrierDistance?.toFixed(2) || '0.00'}%
                      </Text>
                    </View>
                  )}
                  {showAutocallDistance && underlying.autocallDistance != null && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Distance from autocall</Text>
                      <Text style={[styles.value, underlying.autocallDistance < 0 && styles.negativeValue]}>
                        {((underlying.autocallDistance || 0) * 100).toFixed(2)}%
                      </Text>
                    </View>
                  )}
                  {showDistanceCouponBarrier && underlying.couponBarrierDistance && (
                    <View style={styles.row}>
                      <Text style={styles.label}>Coupon barrier distance</Text>
                      <Text style={[styles.value, underlying.couponBarrierDistance < 0 && styles.negativeValue]}>
                        {underlying.couponBarrierDistance?.toFixed(2) || '0.00'}%
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  containerCombined: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  switch: {
    transform: [{ scaleX: 0.9 }, { scaleY: 0.9 }],
  },
  cardsRow: {
    gap: 12,
    paddingRight: 8,
  },
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  card: {
    width: 320,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    padding: 12,
  },
  cardDesktop: {
    flexGrow: 1,
    flexBasis: '24%',
    minWidth: 220,
    maxWidth: 320,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  separator: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },
  content: {
    gap: 12,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 15,
    color: '#757575',
  },
  value: {
    fontSize: 14,
    color: '#212121',
    fontWeight: '600',
  },
  negativeValue: {
    color: '#F44336',
  },
});
